import React, { useState, useEffect, useRef } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Upload, Loader2, Search, Trash2, Copy, Image, FileText, Download, Calendar, HardDrive, X, Check, FolderOpen } from 'lucide-react';
import { toast } from 'sonner';

export const AdminMedia = () => {
  const { apiCall, API } = useAdmin();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewFile, setViewFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const data = await apiCall('/admin/media');
      setFiles(data || []);
    } catch (err) {
      toast.error('Failed to fetch media files');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(fileList).forEach(file => formData.append('files', file));

      const token = localStorage.getItem('rani_admin_token');
      const response = await fetch(`${API}/upload/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data = await response.json();
      toast.success(`${data.uploaded?.length || 0} files uploaded`);
      fetchMedia();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (selectedFiles.length === 0) return;
    
    try {
      await Promise.all(selectedFiles.map(id => 
        apiCall(`/admin/media/${id}`, { method: 'DELETE' })
      ));
      toast.success(`${selectedFiles.length} files deleted`);
      setSelectedFiles([]);
      fetchMedia();
    } catch (err) {
      toast.error('Failed to delete files');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = url.startsWith('/api') ? `${API.replace('/api', '')}${url}` : url;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copied to clipboard');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('ro-RO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileUrl = (url) => {
    if (url?.startsWith('/api')) {
      return `${API.replace('/api', '')}${url}`;
    }
    return url;
  };

  const isImage = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  };

  const filteredFiles = files.filter(file => 
    !searchQuery || 
    file.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    file.original_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-media">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">Media Library</h2>
          <p className="text-muted-foreground">Manage all uploaded images and files</p>
        </div>
        <div className="flex gap-2">
          {selectedFiles.length > 0 && (
            <Button 
              variant="destructive" 
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete ({selectedFiles.length})
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={(e) => handleUpload(e.target.files)}
            className="hidden"
          />
          <Button 
            className="bg-primary" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="mr-2 h-4 w-4" /> Upload Files</>
            )}
          </Button>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <FolderOpen className="h-4 w-4" />
            <span>{files.length} files</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <HardDrive className="h-4 w-4" />
            <span>{formatFileSize(files.reduce((acc, f) => acc + (f.size || 0), 0))} total</span>
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
      </div>

      {/* Files Grid */}
      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : filteredFiles.length === 0 ? (
            <div className="p-12 text-center">
              <Image className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-muted-foreground">No media files found</p>
              <Button 
                className="mt-4" 
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" /> Upload your first file
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {filteredFiles.map((file) => (
                <div 
                  key={file.id} 
                  className={`group relative border rounded-lg overflow-hidden cursor-pointer transition-all ${
                    selectedFiles.includes(file.id) 
                      ? 'ring-2 ring-primary border-primary' 
                      : 'hover:border-primary'
                  }`}
                  onClick={() => toggleFileSelection(file.id)}
                >
                  {/* Selection Checkbox */}
                  <div className={`absolute top-2 left-2 z-10 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    selectedFiles.includes(file.id)
                      ? 'bg-primary border-primary text-white'
                      : 'bg-white/80 border-gray-300 opacity-0 group-hover:opacity-100'
                  }`}>
                    {selectedFiles.includes(file.id) && <Check className="h-3 w-3" />}
                  </div>

                  {/* Preview */}
                  <div className="aspect-square bg-gray-100 flex items-center justify-center">
                    {isImage(file.filename) ? (
                      <img
                        src={getFileUrl(file.url)}
                        alt={file.original_name || file.filename}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                      />
                    ) : (
                      <FileText className="h-12 w-12 text-gray-400" />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="p-2 bg-white">
                    <p className="text-xs font-medium truncate" title={file.original_name || file.filename}>
                      {file.original_name || file.filename}
                    </p>
                    <div className="flex justify-between items-center text-xs text-muted-foreground mt-1">
                      <span>{formatFileSize(file.size)}</span>
                      <span>{file.created_at ? new Date(file.created_at).toLocaleDateString() : ''}</span>
                    </div>
                  </div>

                  {/* Actions Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setViewFile(file);
                      }}
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(file.url);
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Files</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {selectedFiles.length} file(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog open={!!viewFile} onOpenChange={() => setViewFile(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{viewFile?.original_name || viewFile?.filename}</DialogTitle>
          </DialogHeader>
          {viewFile && (
            <div className="space-y-4">
              {isImage(viewFile.filename) ? (
                <div className="flex justify-center bg-gray-100 rounded-lg p-4">
                  <img
                    src={getFileUrl(viewFile.url)}
                    alt={viewFile.original_name || viewFile.filename}
                    className="max-h-96 object-contain"
                  />
                </div>
              ) : (
                <div className="flex justify-center items-center h-48 bg-gray-100 rounded-lg">
                  <FileText className="h-24 w-24 text-gray-400" />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">File Name:</span>
                  <p className="font-medium">{viewFile.original_name || viewFile.filename}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Size:</span>
                  <p className="font-medium">{formatFileSize(viewFile.size)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Type:</span>
                  <p className="font-medium">{viewFile.mime_type || 'Unknown'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Uploaded:</span>
                  <p className="font-medium">{formatDate(viewFile.created_at)}</p>
                </div>
              </div>

              <div>
                <span className="text-muted-foreground text-sm">URL:</span>
                <div className="flex gap-2 mt-1">
                  <Input 
                    value={getFileUrl(viewFile.url)} 
                    readOnly 
                    className="text-xs"
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(viewFile.url)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setViewFile(null)}>Close</Button>
                <a 
                  href={getFileUrl(viewFile.url)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  download
                >
                  <Button>
                    <Download className="mr-2 h-4 w-4" /> Download
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMedia;
